import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Tabs,
    Tab,
} from '@mui/material';
import { useTranslation } from "react-i18next";
import { Podcasts as PodcastsIcon, Business } from '@mui/icons-material';
import Podcasts from '../components/Podcasts';
import Sponsors from '../components/Sponsors';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`explorer-tabpanel-${index}`}
            aria-labelledby={`explorer-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const Explorer: React.FC = () => {
    const { t } = useTranslation("common");
    const [tabValue, setTabValue] = useState(() => {
        // Try to get persisted tab value from localStorage
        const stored = localStorage.getItem('explorer-tab');
        if (stored) {
            try {
                const { value, timestamp } = JSON.parse(stored);
                const now = Date.now();
                const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

                // Check if the stored value is less than 5 minutes old
                if (now - timestamp < fiveMinutes) {
                    return value;
                } else {
                    // Clear expired data
                    localStorage.removeItem('explorer-tab');
                }
            } catch (error) {
                // If parsing fails, remove the invalid data
                localStorage.removeItem('explorer-tab');
            }
        }
        return 0; // Default to first tab
    });

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        // Persist the new tab value with timestamp
        localStorage.setItem('explorer-tab', JSON.stringify({
            value: newValue,
            timestamp: Date.now()
        }));
    }, []);

    return (
        <Container maxWidth="lg">
            <Typography variant="h1" component="h1" gutterBottom>
                {t("nav.explore")}
            </Typography>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="explorer tabs">
                    <Tab
                        label={t("nav.podcasts")}
                        id="explorer-tab-0"
                        aria-controls="explorer-tabpanel-0"
                        icon={<PodcastsIcon />}
                        iconPosition="start"
                    />
                    <Tab
                        label={t("nav.sponsors")}
                        id="explorer-tab-1"
                        aria-controls="explorer-tabpanel-1"
                        icon={<Business />}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {/* Podcasts Tab */}
            <TabPanel value={tabValue} index={0}>
                <Podcasts />
            </TabPanel>

            {/* Sponsors Tab */}
            <TabPanel value={tabValue} index={1}>
                <Sponsors />
            </TabPanel>
        </Container>
    );
};

export default Explorer; 